<template>
  <content-loading v-if="isLoading" />
  <div v-else-if="questionSet">
    <div class="d-flex justify-content-between" style="margin-bottom: 1.70rem;">
      <div class="d-flex">
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="38" viewBox="0 0 40 38" fill="none" style="cursor: pointer;" @click="goBack()">
          <path
            d="M38.75 19.0003H1.25M1.25 19.0003L18.9583 1.29199M1.25 19.0003L18.9583 36.7087"
            stroke="#FF7C48"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <div class="d-flex align-items-center" style="margin-left: 1.25rem; gap: 2.34rem;">
          <div>
            <h1 class="title">{{ $t("question.answering") }}</h1>
          </div>
        </div>
      </div>
      <div>
        <div class="d-flex align-items-center">
          <div class="icon"><i class="iconoir-chat-bubble-question"></i></div>
          <div class="itext">{{ $t("question-set.answered-question-count") }}:</div>
          <div class="inumber">{{ questionSet.answeredQuestionCount }}/{{ questionSet.questionCount }}</div>

          <div class="icon"><i class="iconoir-medal"></i></div>
          <div class="itext">{{ $t("common.point") }}:</div>
          <div class="inumber">{{ questionSet.score }}</div>

          <div class="d-flex align-items-center">
            <div class="assignee-wrapper mt-2">
              <organization-member-select :disabled="!adminResult" :selected="questionSet.assignee" @onSelect="onAssigneeChanged($event, questionSet)" style="min-width: 250px;" />

              <i
                v-if="questionSet.assignee"
                class="bx bx-x hand"
                @click="onAssigneeChanged(undefined, questionSet)"
                v-b-tooltip.hover.top="{ variant: 'main' }"
                :title="$t('question.assignee.remove')"
                style="font-size: 2rem; color: #C3CCE7; "
              ></i>

              <i class="iconoir-info-circle" v-b-tooltip.hover.top="{ variant: 'main' }" :title="$t('question.assignee.text')" style="font-size: 1.125rem; color: #C3CCE7; "></i>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="acard">
      <div class="qtitle mb-4">{{ questionSet.name | translate }}</div>

      <h6>{{ $t("common.description") }}</h6>
      <div class="atext">
        {{ questionSet.description | translate }}
      </div>
    </div>

    <div class="acard" v-if="questionSet.isAssetGroupQuestionSet">
      <div class="qtitle mb-4">{{ $t("asset-group-survey.admin-note") }}</div>

      <div class="atext">
        <textarea :value="adminNote" @input="updateAdminNote($event)" cols="143" rows="10"></textarea>
      </div>
    </div>

    <template v-for="question in questionSet.questions">
      <div :key="question._id + '1'" v-if="question.canDisplay">
        <div class="question">
          <div class="number answered">{{ question.order }}</div>
          <div>
            <div class="text">
              {{ question.name | translate }}
              <div class="text-muted font-size-12">
                <i v-if="question.answerType == 'MULTIPLE'">{{ $t("question.answer-type.multiple") }}</i>
                <i v-if="question.answerType == 'SINGLE'">{{ $t("question.answer-type.single") }}</i>
                <i v-if="question.answerType == 'FREE_TEXT'">{{ $t("question.answer-type.free-text") }}</i>
              </div>
            </div>
            <div v-if="question.answerType != 'FREE_TEXT'" class="answers">
              <label
                v-if="adminResult"
                v-for="option in question.options"
                :key="question._id + option._id"
                class="answer"
                @click="selectOption(question, option)"
                :class="isOptionSelected(question, option) ? 'active' : ''"
              >
                <div class="custom-radio"><i class="iconoir-check"></i></div>
                {{ option.name | translate }}
              </label>
              <label
                v-if="!adminResult"
                v-for="option in question.options"
                :key="question._id + option._id"
                class="answer"
                :class="isOptionSelected(question, option) ? 'active' : ''"
              >
                <div class="custom-radio"><i class="iconoir-check"></i></div>
                {{ option.name | translate }}
              </label>
            </div>
            <div v-else>
              <textarea
                :disabled="!adminResult"
                :value="question.freeTextAnswer"
                @input="updateAnswer($event, question)"
                class="mt-3 text-dark"
                rows="5"
                cols="100"
                :placeholder="$t('question-set.answer')"
              ></textarea>
            </div>
            <textarea
              :disabled="!adminResult"
              :value="question.note && question.note.content"
              @input="updateNote($event, question)"
              class="mt-3"
              cols="53"
              rows="3"
              :placeholder="$t('common.note')"
            ></textarea>

            <i v-if="question.note" class="text-muted">
              {{ question.note.account.name }}
            </i>
          </div>
        </div>
        <hr />
      </div>
    </template>
  </div>
</template>

<script>
import OrganizationMemberSelect from "@cc/organization-member-select";

export default {
  components: { OrganizationMemberSelect },
  props: ["goBack", "adminResult"],
  data() {
    return { sessionId: new Date().getTime() };
  },
  computed: {
    questionSet() {
      return this.$store.getters["organizationQuestionSet/selectedEntity"];
    },
    isLoading() {
      return this.$store.getters["organizationQuestionSet/isLoading"];
    },
    adminNote() {
      return this.prepareFormForEdit(this.questionSet, ["adminNote"]).adminNote;
    },
  },
  mounted() {
    this.fetchDetail();
  },
  watch: {},

  methods: {
    async fetchDetail() {
      try {
        await this.$store.dispatch("organizationQuestionSet/findById", this.questionSet._id);
      } catch (error) {
        if (error.status == 403) {
          setTimeout(() => this.goBack(), 1000);
        }
      }
    },

    async selectOption(question, option) {
      const payload = JSON.parse(JSON.stringify({ question, option, sessionId: this.sessionId }));

      try {
        await this.$store.dispatch("organizationQuestionSet/markAnswer", payload);

        this.$toast.success(this.$t("question.answer-success"));
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    isOptionSelected(question, option) {
      return !!question.answers.find((i) => i.optionId == option._id);
    },

    async onAssigneeChanged(account, questionSet) {
      if (account?._id == questionSet.assignee?._id) {
        return;
      }
      const ok = await this.confirm(this.$t("alert.approve"));
      if (!ok) {
        return;
      }

      try {
        await this.$store.dispatch("organizationQuestionSet/assign", { account, questionSet });

        this.$toast.success(this.$t("common.success"));
        if (account?._id != this.currentAccount._id) {
          this.goBack();
        }
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    updateAdminNote($e) {
      this.debounce(
        async () => {
          try {
            const note = $e.target.value;
            await this.$store.dispatch("organizationQuestionSet/UpdateAdminNote", {
              id: this.questionSet._id,
              note,
            });
            this.$toast.success(this.$t("question.note-saved"));
          } catch (error) {
            this.$handleHttpErrorByStatusCode(error);
          }
        },
        "none",
        2200
      );
    },
    updateNote($e, question) {
      this.debounce(
        async () => {
          try {
            const note = $e.target.value;
            await this.$store.dispatch("organizationQuestionSet/updateNote", {
              question,
              note,
              sessionId: this.sessionId,
            });
            this.$toast.success(this.$t("question.note-saved"));
          } catch (error) {
            this.$handleHttpErrorByStatusCode(error);
          }
        },
        question._id,
        2500
      );
    },

    updateAnswer($e, question) {
      this.debounce(
        async () => {
          try {
            const freeTextAnswer = $e.target.value;
            const payload = JSON.parse(JSON.stringify({ question, freeTextAnswer, sessionId: this.sessionId }));
            await this.$store.dispatch("organizationQuestionSet/markAnswer", payload);
            this.$toast.success(this.$t("question.answer-success"));
          } catch (error) {
            console.error(error);
            this.$handleHttpErrorByStatusCode(error);
          }
        },
        question._id,
        1500
      );
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@modules/question/question-set.scss";
</style>
